// Wolsey delivery-by countdown
// Vanilla rewrite (no spacetime). Mirrors the Steptronic logic:
//  - 14:00 Europe/London cut-off
//  - Mon-Fri working days
//  - Skips Sat/Sun and UK bank holidays
//  - After cut-off: rolls to tomorrow's cut-off (delivery date pushes forward by one working day)

(function () {
  'use strict'

  const CUT_OFF_HOUR = 14 // 14:00
  const CUT_OFF_MINUTE = 0
  const TIMEZONE = 'Europe/London'

  // UK bank holidays (England & Wales) 2026–2027.
  // Update this list annually. Source: gov.uk/bank-holidays
  const BANK_HOLIDAYS = new Set([
    // 2026
    '2026-01-01', // New Year's Day
    '2026-04-03', // Good Friday
    '2026-04-06', // Easter Monday
    '2026-05-04', // Early May
    '2026-05-25', // Spring
    '2026-08-31', // Summer
    '2026-12-25', // Christmas Day
    '2026-12-28', // Boxing Day (substitute)
    // 2027
    '2027-01-01', // New Year's Day
    '2027-03-26', // Good Friday
    '2027-03-29', // Easter Monday
    '2027-05-03', // Early May
    '2027-05-31', // Spring
    '2027-08-30', // Summer
    '2027-12-27', // Christmas Day (substitute)
    '2027-12-28', // Boxing Day (substitute)
  ])

  // Get the current time as { y, m, d, h, mi } in Europe/London regardless of the user's local timezone.
  function nowInLondon() {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date())

    const get = (type) => parseInt(parts.find((p) => p.type === type).value, 10)
    return {
      y: get('year'),
      m: get('month'),
      d: get('day'),
      h: get('hour') === 24 ? 0 : get('hour'), // some engines emit "24" for midnight
      mi: get('minute'),
      s: get('second'),
    }
  }

  // ISO date string yyyy-mm-dd for a Date or {y,m,d} object.
  function isoDate(y, m, d) {
    const pad = (n) => String(n).padStart(2, '0')
    return `${y}-${pad(m)}-${pad(d)}`
  }

  // Day of week for a y-m-d in London (0=Sun, 6=Sat).
  // We build a Date as UTC noon to avoid timezone roll-over.
  function dayOfWeek(y, m, d) {
    return new Date(Date.UTC(y, m - 1, d, 12, 0, 0)).getUTCDay()
  }

  function isWeekend(y, m, d) {
    const dow = dayOfWeek(y, m, d)
    return dow === 0 || dow === 6
  }

  function isBankHoliday(y, m, d) {
    return BANK_HOLIDAYS.has(isoDate(y, m, d))
  }

  function isNonWorkingDay(y, m, d) {
    return isWeekend(y, m, d) || isBankHoliday(y, m, d)
  }

  // Add n calendar days to {y,m,d}, returning a new {y,m,d}.
  function addDays(y, m, d, n) {
    const date = new Date(Date.UTC(y, m - 1, d, 12, 0, 0))
    date.setUTCDate(date.getUTCDate() + n)
    return { y: date.getUTCFullYear(), m: date.getUTCMonth() + 1, d: date.getUTCDate() }
  }

  // Roll forward past any non-working days.
  function rollToNextWorkingDay({ y, m, d }) {
    while (isNonWorkingDay(y, m, d)) {
      ;({ y, m, d } = addDays(y, m, d, 1))
    }
    return { y, m, d }
  }

  // Compute the delivery date based on current London time vs cut-off.
  // workingDays=1 for next-day. If we're past cut-off on a working day, push one more day.
  function getDeliveryDate(now, workingDays) {
    const past = now.h > CUT_OFF_HOUR || (now.h === CUT_OFF_HOUR && now.mi >= CUT_OFF_MINUTE)
    let date = { y: now.y, m: now.m, d: now.d }

    // If we're past cut-off and today is a working day, the order effectively dispatches tomorrow.
    // If today is a non-working day, the order dispatches the next working day anyway, so no penalty.
    let daysToAdd = workingDays
    if (past && !isNonWorkingDay(date.y, date.m, date.d)) {
      daysToAdd += 1
    }

    // Roll the start forward past non-working days first.
    date = rollToNextWorkingDay(date)

    // Then advance the required number of working days.
    for (let i = 0; i < daysToAdd; i++) {
      date = addDays(date.y, date.m, date.d, 1)
      date = rollToNextWorkingDay(date)
    }

    return date
  }

  // Hours/minutes until the next cut-off (today's if not yet passed, tomorrow's if passed).
  function secondsUntilNextCutOff(now) {
    // Build a Date for today's cut-off in London. We approximate by treating the London wall-clock
    // values as if they were on a single timeline; this is exact because we only care about deltas.
    const londonNow = new Date(Date.UTC(now.y, now.m - 1, now.d, now.h, now.mi, now.s))
    let londonCutOff = new Date(Date.UTC(now.y, now.m - 1, now.d, CUT_OFF_HOUR, CUT_OFF_MINUTE, 0))
    if (londonNow >= londonCutOff) {
      londonCutOff = new Date(londonCutOff.getTime() + 24 * 60 * 60 * 1000)
    }
    return Math.floor((londonCutOff - londonNow) / 1000)
  }

  function ordinal(n) {
    if (n > 3 && n < 21) return 'th'
    switch (n % 10) {
      case 1: return 'st'
      case 2: return 'nd'
      case 3: return 'rd'
      default: return 'th'
    }
  }

  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December']

  function formatLongDate({ y, m, d }) {
    const dow = DAY_NAMES[dayOfWeek(y, m, d)]
    return `${dow} ${d}${ordinal(d)} ${MONTH_NAMES[m - 1]}`
  }

  function update() {
    const wrapper = document.querySelector('.c-delivery-by')
    if (!wrapper) return

    const now = nowInLondon()
    const delivery = getDeliveryDate(now, 1)
    const secsRemaining = secondsUntilNextCutOff(now)
    const hours = Math.floor(secsRemaining / 3600)
    const minutes = Math.floor((secsRemaining % 3600) / 60)

    const hEl = wrapper.querySelector('.c-delivery-by__hours')
    const mEl = wrapper.querySelector('.c-delivery-by__minutes')
    const dEl = wrapper.querySelector('.c-delivery-by__date')

    if (hEl) hEl.textContent = hours
    if (mEl) mEl.textContent = minutes
    if (dEl) dEl.textContent = formatLongDate(delivery)

    // Reveal once populated (avoids "0h 0m" flash on first paint).
    wrapper.removeAttribute('hidden')
  }

  let intervalId = null
  function start() {
    update()
    if (intervalId) return
    intervalId = setInterval(update, 30000)
  }
  function stop() {
    if (!intervalId) return
    clearInterval(intervalId)
    intervalId = null
  }

  // Pause when the tab is hidden; resume on return.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') stop()
    else start()
  })

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start)
  } else {
    start()
  }
})()
